/**
 * Shared fetch helpers that eliminate "body stream already read" bugs.
 *
 * readResponse(response, fallbackMessage?)
 *   - Reads the response body exactly ONCE and returns the parsed JSON if
 *     possible, or the raw text otherwise.
 *   - If the response is not OK, throws an Error with the server-provided
 *     `detail` field (or the raw text, or the fallback message).
 *
 * fetchJson(url, init?)
 *   - Wraps fetch() + readResponse() with `credentials: 'include'` by default.
 *   - Use this everywhere that currently does `fetch(...).then(r => r.json())`
 *     or the `if (!r.ok) await r.json(); const data = await r.json()` pattern.
 *
 * These helpers make it impossible to double-read the body and give every
 * caller the same error shape.
 */

export async function readResponse(response, fallbackMessage) {
  // Read once via clone() so we never collide with another fetch interceptor
  // (the Emergent platform's emergent-main.js, sentry shims, devtools, etc)
  // that may have already consumed the original response body.
  let raw = '';
  try {
    raw = await response.clone().text();
  } catch (_) {
    // Clone refused because the original body is already consumed AND gone.
    // Try the original one last time — if that throws too, leave raw empty.
    try { raw = await response.text(); } catch (_) { raw = ''; }
  }
  let parsed;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch (_) {
    parsed = null;
  }

  if (!response.ok) {
    const msg =
      (parsed && (parsed.detail || parsed.message || parsed.error)) ||
      raw ||
      fallbackMessage ||
      `Request failed (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    err.body = parsed ?? raw;
    throw err;
  }

  return parsed ?? raw;
}

export async function fetchJson(url, init = {}) {
  const finalInit = { credentials: 'include', ...init };
  const response = await fetch(url, finalInit);
  return readResponse(response);
}
